import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ConstantsService } from '../../fidelidade/ConstantsService';
import { APRESENTACOES_RECURSOS, ApresentacaoRecurso, TITULOS_RECURSOS } from '../apresentacoes-recursos';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { RotaGuardClient } from '../../guards/auth-loja.guard';
import {
  DescobertaRecursosService,
  ContratacaoRecurso,
  InteresseRecurso,
  ModuloCatalogoCliente,
  OrigemInteresseRecurso,
  RecursoCliente,
  StatusInteresseRecurso
} from '../../services/descoberta-recursos.service';

type AbaRecursos = 'CONHECER' | 'ATIVOS' | 'INTERESSES';

interface ModuloComRecursos {
  modulo: ModuloCatalogoCliente | null;
  principal: RecursoCliente | null;
  recursos: RecursoCliente[];
}

@Component({
  selector: 'app-explorar-recursos',
  templateUrl: './explorar-recursos.component.html',
  styleUrls: ['./explorar-recursos.component.scss']
})
export class ExplorarRecursosComponent implements OnInit, OnDestroy {
  readonly apresentacoes = APRESENTACOES_RECURSOS;
  aba: AbaRecursos = 'CONHECER';
  busca = '';
  moduloFiltro = '';
  recursos: RecursoCliente[] = [];
  interesses: InteresseRecurso[] = [];
  recursoSelecionado: RecursoCliente | null = null;
  empresaId: number | null = null;
  carregando = false;
  salvandoInteresse = false;
  erroCarregamento: string | null = null;
  erroSolicitacao: string | null = null;
  interesseConfirmadoSlug: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private sequenciaConsulta = 0;
  private sequenciaSolicitacao = 0;
  private empresaAtual: any = null;
  private recursoSolicitadoNaUrl: string | null = null;
  private origemDetalhe: OrigemInteresseRecurso = 'catalogo';

  constructor(
    private descobertaRecursosService: DescobertaRecursosService,
    private constantsService: ConstantsService,
    private activatedRoute: ActivatedRoute,
    private autorizacaoService: AutorizacaoService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.recursoSolicitadoNaUrl = params.recurso || null;
        this.origemDetalhe = this.normalizeOrigem(params.origem);
        this.abraDetalheSolicitado();
      });

    this.constantsService.empresa$
      .pipe(takeUntil(this.destroy$))
      .subscribe(empresa => this.carregueParaEmpresa(empresa));
  }

  ngOnDestroy(): void {
    this.sequenciaConsulta++;
    this.sequenciaSolicitacao++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  get recursosParaConhecer(): RecursoCliente[] {
    return this.recursos.filter(recurso => !this.recursoDaLoja(recurso));
  }

  get recursosAtivos(): RecursoCliente[] {
    return this.recursos.filter(recurso => this.recursoDaLoja(recurso));
  }

  get modulosParaConhecer(): ModuloComRecursos[] {
    return this.agrupeModulos(false);
  }

  get modulosAtivos(): ModuloComRecursos[] {
    return this.agrupeModulos(true);
  }

  get modulosVisiveis(): ModuloComRecursos[] {
    const termo = this.normalizeBusca(this.busca);
    return this.modulosDaAba.filter(grupo => !this.moduloFiltro ||
      (grupo.modulo?.slug || 'outros-recursos') === this.moduloFiltro).map(grupo => {
        const correspondeAoModulo = this.correspondeBusca(grupo.principal || grupo.modulo, termo);
        return { ...grupo, recursos: correspondeAoModulo ? grupo.recursos : grupo.recursos.filter(recurso =>
          this.correspondeBusca(recurso, termo)) };
      }).filter(grupo => grupo.recursos.length || this.correspondeBusca(grupo.principal || grupo.modulo, termo));
  }

  get modulosDaAba(): ModuloComRecursos[] {
    return this.aba === 'CONHECER' ? this.modulosParaConhecer : this.aba === 'ATIVOS' ? this.modulosAtivos : [];
  }

  get quantidadeVisivel(): number {
    return this.modulosVisiveis.reduce((total, grupo) => total + grupo.recursos.length +
      (grupo.principal && this.recursoDaLoja(grupo.principal) === (this.aba === 'ATIVOS') ? 1 : 0), 0);
  }

  limpeFiltros(): void {
    this.busca = '';
    this.moduloFiltro = '';
  }

  tituloRecurso(recurso: {slug: string; titulo: string} | null): string {
    return recurso ? TITULOS_RECURSOS[recurso.slug] || recurso.titulo : 'Outros recursos';
  }

  private normalizeBusca(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  private correspondeBusca(recurso: {slug: string; titulo: string; descricaoCurta: string} | null, termo: string): boolean {
    return !termo || this.normalizeBusca(this.tituloRecurso(recurso) + ' ' + (recurso?.descricaoCurta || '')).includes(termo);
  }

  rotaDoRecurso(recurso: RecursoCliente): string | null {
    if (recurso.status !== 'ATIVO') return null;
    const usuario = this.autorizacaoService.getUser();
    const produtos = RotaGuardClient.cadastrarProdutos(usuario);
    const loja = RotaGuardClient.gerenciaLoja(usuario);
    // Mesmas rotas e permissões usadas no menu e em fidelidade.routing.ts.
    const destinos: {[slug: string]: {rota: string; permitido?: boolean; modulo?: string; exigeRecurso?: boolean}} = {
      cardapio_digital: {rota: '/admin/produtos-v2', permitido: produtos, exigeRecurso: false},
      pedidos_delivery: {rota: '/admin/pedidos', modulo: 'pedidos'},
      mesas_garcom: {rota: '/admin/pedidos-mesas', modulo: 'mesas_garcom'},
      fidelidade: {rota: '/admin/fidelidade/pontuacoes', modulo: 'fidelidade'},
      entregas_logistica: {rota: '/admin/entregadores', modulo: 'entregadores', permitido: loja},
      vitrines: {rota: '/admin/vitrines', permitido: produtos},
      cupons_avancados: {rota: '/admin/cupons'},
      estoque: {rota: '/admin/estoque', permitido: RotaGuardClient.gerenciarEstoque(usuario)},
      kds_cozinha: {rota: '/kds/home', permitido: RotaGuardClient.operadorKDS(usuario), exigeRecurso: false},
      tabela_precos: {rota: '/admin/tabelas-precos', permitido: produtos},
      comissionamento: {rota: '/admin/comissionamento', permitido: produtos},
      temas_personalizados: {rota: '/admin/temas', permitido: loja},
      avaliacoes_pedidos: {rota: '/admin/avaliacoes', permitido: loja},
      controle_fiado: {rota: '/admin/fiados'},
      caixa: {rota: '/admin/meupdv'},
      fiscal: {rota: '/admin/fiscal'},
      inteligencia_negocio: {rota: '/admin/relatorios'},
      campanhas_whatsapp: {rota: '/admin/marketing'},
      ferramentas_marketing: {rota: '/admin/scripts-marketing'},
      link_na_bio: {rota: '/admin/bio/configuracoes', permitido: loja},
      mia_atendente: {rota: '/admin/chatbot/configuracoes'},
      chatbot_instagram: {rota: '/admin/chatbot/configuracao-instagram'},
      notificacoes_app_proprio: {rota: '/admin/marketing-app'},
      notificacoes_whatsapp_pedidos: {rota: '/admin/notificacoes', permitido: RotaGuardClient.cadastrarNotificacoes(usuario)}
    };
    const destino = destinos[recurso.slug];
    if (!destino || destino.permitido === false) return null;
    const contextos = [usuario, {empresa: this.empresaAtual}];
    const possuiDireito = destino.exigeRecurso === false || (destino.modulo ? contextos.some(contexto => RotaGuardClient.possuiModulo(contexto, destino.modulo)) :
      contextos.some(contexto => RotaGuardClient.possuiRecurso(contexto, recurso.slug)));
    return possuiDireito ? destino.rota : null;
  }

  identifiqueModulo(_: number, grupo: ModuloComRecursos): string {
    return grupo.modulo?.slug || 'outros-recursos';
  }

  identifiqueRecurso(_: number, recurso: RecursoCliente): string {
    return recurso.slug;
  }

  filtrePorContratacao(recursos: RecursoCliente[], contratacao: ContratacaoRecurso): RecursoCliente[] {
    return recursos.filter(recurso => recurso.contratacao === contratacao);
  }

  rotuloContratacao(recurso: RecursoCliente): string {
    if (recurso.status === 'INCLUIDO') return 'Incluído no seu plano';
    if (recurso.contratacao === 'GRATUITO') return 'Gratuito';
    if (recurso.contratacao === 'CONSULTAR') return 'Condições a confirmar';
    return recurso.tipo === 'MODULO_PRINCIPAL' ? 'Módulo pago' : 'Adicional pago · contratação à parte';
  }

  private recursoDaLoja(recurso: RecursoCliente): boolean {
    return recurso.status === 'ATIVO' || recurso.status === 'INCLUIDO';
  }

  private agrupeModulos(ativos: boolean): ModuloComRecursos[] {
    const grupos = new Map<string | null, ModuloComRecursos>();
    this.recursos.forEach(recurso => {
      const principal = recurso.tipo === 'MODULO_PRINCIPAL';
      const modulo: ModuloCatalogoCliente | null = principal ? {
        slug: recurso.slug, titulo: recurso.titulo, descricaoCurta: recurso.descricaoCurta,
        categoria: recurso.categoria, ordem: recurso.ordem, gratuito: recurso.gratuito, agrupador: false
      } : recurso.recursoPai || null;
      const chave = modulo?.slug || null;
      if (!grupos.has(chave)) grupos.set(chave, { modulo, principal: null, recursos: [] });
      const grupo = grupos.get(chave);
      if (principal) {
        grupo.modulo = modulo;
        grupo.principal = recurso;
      } else {
        grupo.recursos.push(recurso);
      }
    });

    return Array.from(grupos.values()).map(grupo => ({
      ...grupo,
      recursos: grupo.recursos.filter(recurso => this.recursoDaLoja(recurso) === ativos)
        .sort((a, b) => Number(a.gratuito) - Number(b.gratuito))
    })).filter(grupo => grupo.recursos.length || (grupo.principal && this.recursoDaLoja(grupo.principal) === ativos))
      .sort((a, b) => {
        if (!a.modulo) return b.modulo ? 1 : 0;
        if (!b.modulo) return -1;
        return Number(a.modulo.gratuito) - Number(b.modulo.gratuito) || (a.modulo.ordem || 0) - (b.modulo.ordem || 0);
      });
  }

  get apresentacaoSelecionada(): ApresentacaoRecurso | null {
    return this.recursoSelecionado ? APRESENTACOES_RECURSOS[this.recursoSelecionado.slug] || null : null;
  }

  selecioneAba(aba: AbaRecursos): void {
    this.aba = aba;
    this.limpeFiltros();
    this.interesseConfirmadoSlug = null;
  }

  abraDetalhes(recurso: RecursoCliente, origem?: OrigemInteresseRecurso): void {
    this.recursoSelecionado = recurso;
    if (origem) this.origemDetalhe = origem;
    this.erroSolicitacao = null;
    this.interesseConfirmadoSlug = null;
  }

  fecheDetalhes(): void {
    this.recursoSelecionado = null;
    this.erroSolicitacao = null;
    this.interesseConfirmadoSlug = null;
  }

  podeSolicitar(recurso: RecursoCliente | null): boolean {
    return !!recurso && recurso.status === 'DISPONIVEL' && !this.obtenhaInteresseAberto(recurso.slug);
  }

  recarregue(): Promise<void> {
    return this.carregueParaEmpresa(this.empresaAtual);
  }

  obtenhaInteresseAberto(slug: string): InteresseRecurso | null {
    return this.interesses.find(interesse =>
      interesse.recurso.slug === slug && interesse.status !== 'CONCLUIDO'
    ) || null;
  }

  obtenhaRecursoDoInteresse(interesse: InteresseRecurso): RecursoCliente | null {
    return this.recursos.find(recurso => recurso.slug === interesse.recurso.slug) || null;
  }

  async soliciteInteresse(): Promise<void> {
    const recurso = this.recursoSelecionado;
    if (!this.podeSolicitar(recurso) || this.salvandoInteresse) return;

    this.salvandoInteresse = true;
    this.erroSolicitacao = null;
    this.interesseConfirmadoSlug = null;
    const empresaId = this.empresaId;
    const sequencia = ++this.sequenciaSolicitacao;

    try {
      const interesse = await this.descobertaRecursosService.registreInteresse(recurso.slug, this.origemDetalhe);
      if (sequencia !== this.sequenciaSolicitacao || empresaId !== this.empresaId) return;

      this.interesses = [
        interesse,
        ...this.interesses.filter(item => item.recurso.slug !== interesse.recurso.slug)
      ];
      this.interesseConfirmadoSlug = recurso.slug;
    } catch (erro) {
      if (sequencia === this.sequenciaSolicitacao && empresaId === this.empresaId
        && this.recursoSelecionado?.slug === recurso.slug) {
        this.erroSolicitacao = this.obtenhaMensagemErro(erro, 'Não foi possível registrar seu interesse. Tente novamente.');
      }
    } finally {
      if (sequencia === this.sequenciaSolicitacao) this.salvandoInteresse = false;
    }
  }

  iconeRecurso(recurso: Pick<RecursoCliente, 'slug'>): string {
    const icones: { [slug: string]: string } = {
      cardapio_digital: 'fa-book-open',
      pedidos_delivery: 'fa-shopping-bag',
      marketing_relacionamento: 'fa-bullhorn',
      atendimento_automatizado: 'fa-comments',
      mesas_garcom: 'fa-utensils',
      app_proprio: 'fa-mobile-alt',
      pdv: 'fa-cash-register',
      gestao_avancada: 'fa-chart-line',
      fidelidade: 'fa-award',
      entregas_logistica: 'fa-motorcycle',
      pagamentos_eletronicos: 'fa-credit-card',
      estoque: 'fa-boxes',
      kds_cozinha: 'fa-desktop',
      dominio_proprio: 'fa-globe',
      fiscal: 'fa-file-invoice',
      entregadores: 'fa-motorcycle',
      comissionamento: 'fa-percent'
    };
    return icones[recurso.slug] || 'fa-puzzle-piece';
  }

  rotuloStatusInteresse(status: StatusInteresseRecurso): string {
    return ({
      NOVO: 'Aguardando contato',
      EM_CONTATO: 'Em contato',
      CONCLUIDO: 'Concluído'
    })[status];
  }

  private async carregueParaEmpresa(empresa: any): Promise<void> {
    const sequencia = ++this.sequenciaConsulta;
    this.sequenciaSolicitacao++;
    this.salvandoInteresse = false;
    this.empresaAtual = empresa || null;
    this.limpeFiltros();
    this.empresaId = empresa?.id || null;
    this.recursos = [];
    this.interesses = [];
    this.recursoSelecionado = null;
    this.erroCarregamento = null;
    this.interesseConfirmadoSlug = null;
    if (!this.empresaId) {
      this.carregando = false;
      return;
    }

    this.carregando = true;
    try {
      const catalogo = await this.descobertaRecursosService.obtenhaCatalogo();
      if (sequencia !== this.sequenciaConsulta || empresa.id !== this.empresaId) return;

      this.recursos = catalogo.recursos || [];
      this.interesses = catalogo.interesses || [];
      this.abraDetalheSolicitado();
    } catch (erro) {
      if (sequencia === this.sequenciaConsulta) {
        this.erroCarregamento = this.obtenhaMensagemErro(erro, 'Não foi possível carregar os recursos. Tente novamente.');
      }
    } finally {
      if (sequencia === this.sequenciaConsulta) this.carregando = false;
    }
  }

  private abraDetalheSolicitado(): void {
    if (!this.recursoSolicitadoNaUrl || !this.recursos.length) return;

    const recurso = this.recursos.find(item => item.slug === this.recursoSolicitadoNaUrl);
    if (recurso) this.abraDetalhes(recurso, this.origemDetalhe);
    this.recursoSolicitadoNaUrl = null;
  }

  private normalizeOrigem(origem: string): OrigemInteresseRecurso {
    const origens: OrigemInteresseRecurso[] = ['catalogo', 'produtos', 'pedidos', 'loja', 'dashboard'];
    return origens.indexOf(origem as OrigemInteresseRecurso) >= 0
      ? origem as OrigemInteresseRecurso
      : 'catalogo';
  }

  private obtenhaMensagemErro(erro: any, padrao: string): string {
    return typeof erro === 'string' ? erro : erro?.message || padrao;
  }
}
